﻿import { bind } from '../../../vendors/hyperhtml';
import getControlMetadata from './../cuiControls';
import cuiMethods from './../cuiMethods';
import CuiObserver from './../CuiObserver';
import store from '../../store/index';
import { produce } from '../../../vendors/immer';
import itemTypeMetadata from '../../metadata/itemType';

const normalizeControlData = (control) => {
	const result = {
		...control,
		...control.additional_data
	};
	if (result.roots) {
		result.children = result.roots;
		delete result.data;
		delete result.roots;
	}

	return result;
};

const collectControlsData = (controls) => {
	const topToolbarsNode = document.getElementById('top-toolbars');
	const firstNonToolbarControlIndex = topToolbarsNode
		? controls.findIndex(({ control_type: type }) => {
				return type !== 'ToolbarControl' && type !== 'ShortcutsControl';
		  })
		: 0;
	return controls.reduce(
		(controlsData, control, index) => {
			const {
				dataMap,
				firstNonToolbarControlIndex,
				otherControls,
				shortcutControls,
				topToolbars
			} = controlsData;
			const { data, name, control_type: controlType } = control;
			if (controlType === 'ShortcutsControl') {
				shortcutControls.push(name);
			} else if (
				index < firstNonToolbarControlIndex ||
				firstNonToolbarControlIndex === -1
			) {
				topToolbars.push(name);
			} else {
				otherControls.push(name);
			}

			if (data) {
				data.forEach((child) => {
					dataMap.set(child.name, normalizeControlData(child));
				});
			}
			dataMap.set(name, normalizeControlData(control));

			return controlsData;
		},
		{
			dataMap: new Map(),
			firstNonToolbarControlIndex,
			otherControls: [],
			shortcutControls: [],
			topToolbars: []
		}
	);
};

export default class CuiLayout {
	deferredLocations = [];
	observer = new CuiObserver();
	#state = null;
	#props = null;
	#storeLayoutId;
	#subscriptions = [];
	#unloadHandler;

	constructor(dom, location = '', options = {}) {
		this.dom = dom;
		this.location = location;
		this.options = {
			...options,
			location,
			getActions: () => this.actions,
			getProps: () => this.#props,
			getState: () => this.#state,
			notifyObserver: (eventType) => this.observer.notify(eventType)
		};
		this.#storeLayoutId = `${this.constructor.name}_${
			this.location
		}_${Date.now()}`;

		this.#unloadHandler = () => {
			throw new Error(`
				CUI Layout ${this.#storeLayoutId}
				was not destroyed properly. Please call .destroy() to avoid
				memory leaks and forgotten kept data.
			`);
		};

		setTimeout(() => {
			window.addEventListener('unload', this.#unloadHandler);
		}, 0);
	}

	initializePropsAndState() {
		this._initializeState();
		this._initializeProps();

		if (this.#state || this.#props) {
			this._initializeStoreSubscription();
		}
	}

	async init() {
		this.initializePropsAndState();
		const { itemTypeName } = this.options;
		const initItemTypePromise = this.initializeItemTypeMetadata(itemTypeName);

		const controlsPromise = this._getCuiControls();

		const controls = await controlsPromise;
		const {
			dataMap,
			otherControls,
			topToolbars,
			shortcutControls
		} = collectControlsData(controls);
		const controlsMap = new Map();
		if (topToolbars.length) {
			const node = document.getElementById('top-toolbars');
			const controls = topToolbars.map((controlName) => {
				return this._createCuiControl(controlName, dataMap, controlsMap);
			});

			bind(node)`${controls}`;
		}

		if (otherControls.length) {
			const node = this.dom;
			const controls = otherControls.map((controlName) => {
				return this._createCuiControl(controlName, dataMap, controlsMap);
			});

			bind(node)`${controls}`;
		}

		shortcutControls.forEach((name) => {
			this._createCuiControl(name, dataMap, controlsMap);
		});
		await initItemTypePromise;
		const commandFactory = cuiMethods.getLayoutCommandFactory(this.options);
		this.options.commandFactory = commandFactory;
		const initPromise = this._initCuiControls(controlsMap, dataMap);
		this._preloadDeferredLocations();
		await initPromise;
	}

	async initializeItemTypeMetadata(itemTypeName) {
		if (!itemTypeName) {
			return;
		}

		const metadata = await this.getItemTypeMetadata(itemTypeName);
		if (!metadata.polySources) {
			delete metadata.polySources;
		}
		if (!metadata.foreignPropertySources) {
			delete metadata.foreignPropertySources;
		}
		Object.assign(this.options, metadata);
	}

	async getItemTypeMetadata(itemTypeName) {
		const itemType = await itemTypeMetadata.getItemType(itemTypeName);
		const getSourcePropertiesPromise = itemTypeMetadata.getForeignPropertiesSources(
			itemType
		);
		const getPolySourcesPromise = itemTypeMetadata.getItemTypesPolysources(
			itemType
		);
		const [polySources, foreignPropertySources] = await Promise.all([
			getPolySourcesPromise,
			getSourcePropertiesPromise
		]);

		return { itemType, polySources, foreignPropertySources };
	}

	get state() {
		return this.#state;
	}

	get props() {
		return this.#props;
	}

	_initializeStoreSubscription() {
		const unsubscribeFromStore = store.subscribe(() => {
			if (this.#subscriptions.length === 0) {
				return;
			}
			const prevProps = this.#props;
			const prevState = this.#state;

			const state = store.getState();
			const nextDerivedState = state.layout[this.#storeLayoutId];
			this.#state = nextDerivedState;

			const nextProps = this.mapStateToProps(state);
			this.#props = nextProps;

			if (this.shouldLayoutUpdate(prevProps, prevState)) {
				this.updateLayout(prevProps, prevState);
			}
			if (this.state !== prevState) {
				this.observer.notify('UpdateState');
			}
		});

		this.#subscriptions.push(unsubscribeFromStore);
	}

	_initializeState() {
		if (!this.initialState) {
			return;
		}

		this.#state = this.initialState;
		this.setState();
	}

	_initializeProps() {
		const initialProps = this.mapStateToProps(store.getState());

		this.#props = initialProps;
	}

	setState(handler = () => {}) {
		const data = produce(this.#state, (draft) => handler(draft));
		store.boundActionCreators.updateLayoutData(this.#storeLayoutId, data);
	}

	shouldLayoutUpdate(prevProps, prevState) {
		return this.#props !== prevProps || this.#state !== prevState;
	}

	mapStateToProps(state) {}

	updateLayout(prevProps, prevState) {}

	_createCuiControl(controlName, dataMap, controlsMap) {
		const control = dataMap.get(controlName);
		const { children = [], control_type: type } = control;
		const childNodes = children.map((childName) => {
			return this._createCuiControl(childName, dataMap, controlsMap);
		});
		const controlMetadata = getControlMetadata(type);
		const nodes = controlMetadata.constructor(
			control,
			childNodes,
			controlMetadata
		);
		const controlNode = Array.isArray(nodes) ? nodes[0] : nodes;
		controlsMap.set(control, controlNode);
		return nodes;
	}

	async _getCuiControls() {
		const requestParams = await cuiMethods.getRequestParams(
			this.location,
			this.options
		);
		return aras.MetadataCacheJson.GetConfigurableUIControls(requestParams);
	}

	async _initCuiControls(controlsMap, dataMap) {
		const initializedControlsPromises = [];
		let defferedEvents = new Set();

		for (const [componentData, component] of controlsMap) {
			const { control_type: type, children = [] } = componentData;
			const { eventHandler, initControl } = getControlMetadata(type);

			if (eventHandler) {
				const unsubscribe = this.observer.subscribe((eventType) => {
					if (!defferedEvents) {
						eventHandler(component, eventType, this.options);
					} else {
						defferedEvents.add(eventType);
					}
				});
				this.#subscriptions.push(unsubscribe);
			}

			if (initControl) {
				const childrenData = children.map((childName) =>
					dataMap.get(childName)
				);
				const data = { componentData, childrenData };
				const nodes = {
					component,
					children: childrenData.map((childData) => controlsMap.get(childData))
				};
				const initPromise = initControl(nodes, data, this.options);
				initializedControlsPromises.push(initPromise);
			}
		}

		const controls = await Promise.all(initializedControlsPromises);
		controls.forEach((control) => {
			const destroy = control?.destroy;
			if (!destroy) {
				return;
			}

			this.#subscriptions.push(destroy);
		});
		const setEvents = defferedEvents;
		defferedEvents = null;
		setEvents.forEach((eventType) => this.observer.notify(eventType));
	}

	_preloadDeferredLocations() {
		const preload = (location) =>
			cuiMethods.getConfigurableUi(location, this.options);
		const preloadedLocations = this.deferredLocations.map(preload);
		return Promise.all(preloadedLocations);
	}

	destroy() {
		this.#subscriptions.forEach((unsubscribe) => unsubscribe());
		this.#subscriptions = [];

		if (this.#state) {
			store.boundActionCreators.removeLayout(this.#storeLayoutId);
		}

		window.removeEventListener('unload', this.#unloadHandler);
	}
}
